import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useWorkflow } from '../contexts/WorkflowContext.jsx';
import DeployChat from '../components/deploy/DeployChat.jsx';
import DeployProgress from '../components/deploy/DeployProgress.jsx';
import DeployPreview from '../components/deploy/DeployPreview.jsx';
import DeploySuccess from '../components/deploy/DeploySuccess.jsx';
import FileTree from '../components/deploy/FileTree.jsx';
import FilePreviewPanel from '../components/deploy/FilePreviewPanel.jsx';
import api from '../utils/api.js';
import { createLogger } from '../utils/logger.js';
import {
  Container,
  Card,
  Button,
  Heading,
  Text,
  Spinner,
  Alert,
  Icon,
  Badge,
  MobileBottomNav
} from '../components/ui/index.jsx';

const log = createLogger('Deploy');

export default function Deploy() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, authType } = useAuth();

  // WorkflowContext - same agent, same WebSocket
  const {
    workflowState,
    startDeployment,
    sendStartDeploy,
    sendDeployChat,
    approveDeploy,
    pauseDeploy,
    resumeDeploy,
  } = useWorkflow();

  // Extract deployment state from workflowState
  const deployment = workflowState.deployment || {};
  const isConnected = workflowState.wsConnected;
  const phase = deployment.status || 'idle';
  const generationPhase = deployment.generationPhase;
  const previewUrl = deployment.previewUrl;
  const deployedUrl = deployment.deployedUrl;
  const githubUrl = deployment.githubUrl;
  const deployError = deployment.error;
  const files = deployment.files || {};
  const generatedFiles = Object.keys(files);
  const chatHistory = deployment.chatHistory || [];

  // Streaming progress state
  const currentFileName = deployment.currentFileName || null;
  const currentFileIndex = deployment.currentFileIndex || 0;
  const totalFilesInPhase = deployment.totalFilesInPhase || 0;
  const currentFileList = deployment.currentFileList || [];

  // Session data
  const [sessionData, setSessionData] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);

  // Credentials (from previous wizard step)
  const [credentials, setCredentials] = useState(null);
  const [credentialsLoading, setCredentialsLoading] = useState(true);

  // Deployment initialization state
  const [deploymentStarted, setDeploymentStarted] = useState(false);
  const [isResumedDeployment, setIsResumedDeployment] = useState(false);
  const [initError, setInitError] = useState(null);

  // Chat input state
  const [chatInput, setChatInput] = useState('');
  const [isSending, setIsSending] = useState(false);

  // File preview state
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileContent, setSelectedFileContent] = useState('');
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Mobile bottom nav items
  const mobileNavItems = useMemo(() => [
    { id: 'home', icon: 'home', label: 'Home', path: '/', onClick: () => navigate('/') },
    { id: 'dashboard', icon: 'layout-dashboard', label: 'Dashboard', path: '/dashboard', onClick: () => navigate('/dashboard') },
    { id: 'deploy', icon: 'rocket', label: 'Deploy', path: `/deploy/${sessionId}`, active: true }
  ], [navigate, sessionId]);

  // Load session data
  useEffect(() => {
    async function loadSession() {
      if (!sessionId) return;

      try {
        setSessionLoading(true);
        const response = await api.getSession(sessionId);

        if (response.session) {
          setSessionData(response.session);
        } else {
          setSessionError(response.error || 'Failed to load session');
        }
      } catch (err) {
        log.error('Failed to load session:', err);
        setSessionError(err.message || 'Failed to load session');
      } finally {
        setSessionLoading(false);
      }
    }

    loadSession();
  }, [sessionId]);

  // Load stored credentials (from CloudflareDeployWizard via connection-status endpoint)
  useEffect(() => {
    async function loadCredentials() {
      if (!sessionId) return;

      try {
        setCredentialsLoading(true);
        const response = await api.request('/api/v2/deploy/connection-status', { method: 'GET' });

        if (response.github?.connected && response.cloudflare?.connected) {
          setCredentials({
            githubConnected: true,
            cloudflareConnected: true
          });
        }
      } catch (err) {
        log.error('Failed to load credentials:', err);
      } finally {
        setCredentialsLoading(false);
      }
    }

    loadCredentials();
  }, [sessionId]);

  // Initialize deployment via HTTP endpoint (returns agentId)
  useEffect(() => {
    async function initDeployment() {
      if (!sessionId || !credentials || deploymentStarted) return;

      try {
        log.info('Initializing deployment via HTTP...');
        const response = await api.request('/api/v2/deploy/start', {
          method: 'POST',
          body: JSON.stringify({ sessionId })
        });

        if (response.success) {
          log.info('Deployment initialized:', response);

          // If resumed deployment, fetch files from backend
          let restoredFiles = {};
          if (response.resumed && response.deploymentId) {
            log.info('Resumed deployment detected, fetching files...');
            try {
              const filesResponse = await api.request(`/api/v2/deploy/${response.deploymentId}/files`);
              if (filesResponse.success && filesResponse.files) {
                restoredFiles = filesResponse.files;
                log.info(`Restored ${Object.keys(restoredFiles).length} files from database`);
              }
            } catch (filesErr) {
              log.warn('Failed to fetch files on resume:', filesErr);
              // Continue without files - they may sync via WebSocket
            }
          }

          // Start deployment in WorkflowContext (connects WebSocket)
          startDeployment({
            deploymentId: response.deploymentId,
            agentId: response.agentId,
            sessionId,
            status: response.status,
            phase: response.phase,
            files: restoredFiles,
            filesCount: response.filesCount || Object.keys(restoredFiles).length,
            previewUrl: response.previewUrl,
            deployedUrl: response.deployedUrl,
            githubUrl: response.githubUrl,
          });
          setDeploymentStarted(true);
          setIsResumedDeployment(!!response.resumed);
        } else {
          setInitError(response.error || 'Failed to initialize deployment');
        }
      } catch (err) {
        log.error('Failed to initialize deployment:', err);
        setInitError(err.message || 'Failed to initialize deployment');
      }
    }

    initDeployment();
  }, [sessionId, credentials, deploymentStarted, startDeployment]);

  // Auto-start deployment ONLY for fresh deployments (initialized)
  // For failed/paused, user must click Resume/Retry button
  useEffect(() => {
    if (isConnected && deploymentStarted && phase === 'initialized' && !isResumedDeployment) {
      log.info('WebSocket connected, sending start_deploy (fresh deployment)...');
      sendStartDeploy({
        sessionId,
        resume: false,
        githubConnected: credentials?.githubConnected,
        cloudflareConnected: credentials?.cloudflareConnected
      });
    }
  }, [isConnected, deploymentStarted, phase, isResumedDeployment, sendStartDeploy, sessionId, credentials]);

  // Handle chat submit
  const handleChatSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || isSending) return;

    const message = chatInput.trim();
    setChatInput('');
    setIsSending(true);

    try {
      sendDeployChat(message);
    } finally {
      setIsSending(false);
    }
  }, [chatInput, isSending, sendDeployChat]);

  // Handle deploy to production
  const handleDeployToProduction = useCallback(() => {
    approveDeploy();
  }, [approveDeploy]);

  // Handle pause
  const handlePause = useCallback(() => {
    pauseDeploy();
  }, [pauseDeploy]);

  // Handle resume
  const handleResume = useCallback(() => {
    resumeDeploy();
  }, [resumeDeploy]);

  // Handle file select for preview
  const handleFileSelect = useCallback((path, content) => {
    setSelectedFile(path);
    setSelectedFileContent(content || files[path] || '');
    setShowFilePreview(true);
  }, [files]);

  // Handle copy success
  const handleCopySuccess = useCallback(() => {
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  }, []);

  // Loading state
  if (authLoading || sessionLoading || credentialsLoading) {
    return (
      <div className="min-h-screen bg-[#0D0D10]">
        <Navigation />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <Spinner size="lg" />
            <Text className="mt-4 text-gray-400">Loading deployment...</Text>
          </div>
        </div>
        <MobileBottomNav items={mobileNavItems} />
      </div>
    );
  }

  // Error state
  if (sessionError || initError) {
    return (
      <div className="min-h-screen bg-[#0D0D10]">
        <Navigation />
        <Container className="py-12">
          <Alert variant="error">
            <Text>{sessionError || initError}</Text>
            <Button
              variant="secondary"
              size="sm"
              className="mt-4"
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </Button>
          </Alert>
        </Container>
        <MobileBottomNav items={mobileNavItems} />
      </div>
    );
  }

  // Success state
  if (phase === 'completed' && deployedUrl) {
    return (
      <div className="min-h-screen bg-[#0D0D10]">
        <Navigation />
        <Container className="py-8 max-w-4xl">
          <DeploySuccess
            deployedUrl={deployedUrl}
            githubUrl={githubUrl}
            sessionData={sessionData}
            onViewDashboard={() => navigate('/dashboard')}
          />
        </Container>
        <MobileBottomNav items={mobileNavItems} />
      </div>
    );
  }

  // Map deployment messages to chat format (preserve role for proper styling)
  const messages = chatHistory.map((msg, i) => ({
    id: i,
    role: msg.role || 'system',
    content: msg.content,
    timestamp: msg.timestamp,
    file: msg.file,
    previewUrl: msg.previewUrl,
    deployedUrl: msg.deployedUrl,
    githubUrl: msg.githubUrl,
    error: msg.error
  }));

  return (
    <div className="min-h-screen bg-[#0D0D10]">
      <Navigation />

      <Container className="py-6 max-w-7xl">
        {/* Header with action buttons */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <Heading level={1} className="text-white">
                Building Your App
              </Heading>
              <Text className="text-gray-400 mt-1 truncate">
                {sessionData?.idea?.substring(0, 100)}
                {sessionData?.idea?.length > 100 ? '...' : ''}
              </Text>
            </div>

            {/* Action buttons based on phase */}
            <div className="flex items-center gap-2 flex-shrink-0 ml-4">
              {/* During generation/building - show Pause only */}
              {['generating', 'building'].includes(phase) && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handlePause}
                  className="text-yellow-400 border-yellow-400/30 hover:bg-yellow-400/10"
                >
                  <Icon name="pause" size="sm" className="mr-1" />
                  Pause
                </Button>
              )}

              {/* When paused - show Resume only */}
              {phase === 'paused' && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleResume}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Icon name="play" size="sm" className="mr-1" />
                  Resume
                </Button>
              )}

              {/* When failed - show Retry button */}
              {phase === 'failed' && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleResume}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Icon name="refresh-cw" size="sm" className="mr-1" />
                  Retry
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Paused banner */}
        {phase === 'paused' && (
          <Alert variant="warning" className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="pause-circle" size="sm" />
                <Text>Deployment paused at {deployment.pausedPhase} phase ({generatedFiles.length} files generated)</Text>
              </div>
              <Button variant="primary" size="sm" onClick={handleResume}>
                <Icon name="play" size="sm" className="mr-1" />
                Resume
              </Button>
            </div>
          </Alert>
        )}

        {/* Progress Bar */}
        <DeployProgress
          phase={phase}
          generationPhase={generationPhase}
          filesCount={generatedFiles.length}
          currentFileName={currentFileName}
          currentFileIndex={currentFileIndex}
          totalFilesInPhase={totalFilesInPhase}
          currentFileList={currentFileList}
        />

        {/* Main Content Grid - 3 panels */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          {/* Left: File Tree */}
          <Card className="bg-[#1A1A24] border-[#2A2A3C] p-0 overflow-hidden flex flex-col" style={{ height: '500px' }}>
            <div className="px-4 py-3 border-b border-[#2A2A3C] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="folder-tree" size="sm" className="text-[#7B61FF]" />
                <Text className="font-medium text-white">Files</Text>
                <Badge variant="default" size="sm">{generatedFiles.length}</Badge>
              </div>
            </div>
            <FileTree
              files={files}
              onFileSelect={handleFileSelect}
              selectedFile={selectedFile}
              currentFileName={currentFileName}
              className="flex-1"
            />
          </Card>

          {/* Center: Chat / Activity Log */}
          <Card className="bg-[#1A1A24] border-[#2A2A3C] p-0 overflow-hidden flex flex-col lg:col-span-2" style={{ height: '500px' }}>
            <div className="px-4 py-3 border-b border-[#2A2A3C] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="message-circle" size="sm" className="text-[#7B61FF]" />
                <Text className="font-medium text-white">Activity</Text>
              </div>
              {isConnected ? (
                <Badge variant="success" size="sm">Connected</Badge>
              ) : deploymentStarted ? (
                <Badge variant="warning" size="sm">Connecting...</Badge>
              ) : (
                <Badge variant="default" size="sm">Initializing...</Badge>
              )}
            </div>

            {/* Chat Messages */}
            <DeployChat
              messages={messages}
              isLoading={phase === 'generating' || phase === 'building'}
            />

            {/* Chat Input (only during preview phase) */}
            {phase === 'preview' && (
              <form onSubmit={handleChatSubmit} className="p-4 border-t border-[#2A2A3C]">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Request changes to your app..."
                    className="flex-1 bg-[#0D0D10] border border-[#2A2A3C] rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#7B61FF] focus:border-transparent"
                    disabled={isSending}
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={!chatInput.trim() || isSending}
                  >
                    <Icon name="send" size="sm" />
                  </Button>
                </div>
              </form>
            )}
          </Card>
        </div>

        {/* File Preview Panel (below, when a file is selected) */}
        {showFilePreview && selectedFile && (
          <Card className="bg-[#1A1A24] border-[#2A2A3C] p-0 overflow-hidden mt-6" style={{ height: '350px' }}>
            <FilePreviewPanel
              filePath={selectedFile}
              content={selectedFileContent}
              onClose={() => setShowFilePreview(false)}
              onCopy={handleCopySuccess}
            />
            {copySuccess && (
              <div className="absolute top-4 right-16 bg-green-500 text-white px-3 py-1 rounded text-sm">
                Copied!
              </div>
            )}
          </Card>
        )}

        {/* Preview iframe (when preview is ready) */}
        {previewUrl && (
          <Card className="bg-[#1A1A24] border-[#2A2A3C] p-0 overflow-hidden flex flex-col mt-6" style={{ height: '500px' }}>
            <div className="px-4 py-3 border-b border-[#2A2A3C] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="eye" size="sm" className="text-[#7B61FF]" />
                <Text className="font-medium text-white">Live Preview</Text>
              </div>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => window.open(previewUrl, '_blank')}
              >
                <Icon name="external-link" size="xs" className="mr-1" />
                Open in New Tab
              </Button>
            </div>
            <DeployPreview
              previewUrl={previewUrl}
              phase={phase}
              generationPhase={generationPhase}
              filesCount={generatedFiles.length}
            />
          </Card>
        )}

        {/* Deploy Button (only when preview is ready) */}
        {phase === 'preview' && (
          <div className="mt-6 flex justify-center">
            <Button
              variant="primary"
              size="lg"
              onClick={handleDeployToProduction}
              className="px-12 py-4 text-lg font-semibold bg-gradient-to-r from-[#7B61FF] to-[#BA87FF] hover:from-[#8B71FF] hover:to-[#CA97FF]"
            >
              <Icon name="rocket" size="md" className="mr-2" />
              Deploy to Production
            </Button>
          </div>
        )}

        {/* Deploying state */}
        {phase === 'deploying' && (
          <div className="mt-6 flex justify-center">
            <Card className="bg-[#1A1A24] border-[#2A2A3C] px-8 py-6 text-center">
              <Spinner size="lg" className="mx-auto" />
              <Text className="mt-4 text-white font-medium">Deploying to production...</Text>
              <Text className="mt-2 text-gray-400 text-sm">
                Creating resources, pushing to GitHub, and deploying to Cloudflare Pages
              </Text>
            </Card>
          </div>
        )}

        {/* Error state */}
        {deployError && phase === 'failed' && (
          <Alert variant="error" className="mt-6">
            <div className="flex items-center justify-between">
              <div>
                <Text className="font-medium">Deployment failed</Text>
                <Text className="text-sm mt-1">{deployError}</Text>
              </div>
              <Button variant="primary" size="sm" onClick={handleResume} className="bg-orange-600 hover:bg-orange-700">
                <Icon name="refresh-cw" size="sm" className="mr-1" />
                Retry
              </Button>
            </div>
          </Alert>
        )}
      </Container>

      <MobileBottomNav items={mobileNavItems} />
    </div>
  );
}
